import types from '../types'
import moment from 'moment'
import { toObj } from 'common/utils/arrayUtils'

import { DEFAULT_SEARCH_HOUR, DEFAULT_MODE_IDS, FERRY_TYPES } from 'tickets/utils/constants'
import { getDestinationGooglePlaceId, getDestinationLongName, getOriginGooglePlaceId, getOriginLongName } from '../location/selectors'

const createNewFilter = (modeId) => ({
  name: modeId,
  outboundDepartureTime: null,
  inboundDepartureTime: null,
  ferryType: null,
})

const defaultDates = {}

const initialState = toObj(DEFAULT_MODE_IDS, modeId => modeId, createNewFilter)

const filtersReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.OUTBOUND_DEPARTURE_TIME_INITIALIZED:
      return setAllDepartureTimes(state, action.time, true)
    case types.INBOUND_DEPARTURE_TIME_INITIALIZED:
      return setAllDepartureTimes(state, action.time, false)
    case types.TIME_CHANGE:
      return setModeTime(state, action.modeId, action.isOutbound, action.newDateTime)
    case types.FERRY_TYPE_CHANGE:
      return setFerryType(state, action.modeId, action.ferryType)
    case types.CONFIG_RECEIVED:
      return handleConfigReceived(state, action.payload, action.extraParams)
    case types.UPDATE_TRANSFER:
      return setTransfer(state, action)
    case types.SEARCH_PARAMS_UPDATE:
      setDefaultDate(action.outboundDateTime, action.inboundDateTime)
      return state
    default:
      return state
  }
}

const setTransfer = (
  state,
  action
) => {
  const {
    modeId,
    isDropOff,
    transferLongName,
    transferGooglePlaceId,
    departureDateTime,
    returnDateTime,
  } = action

  const newFilter = {
    ...state[modeId],
  }

  newFilter.updatedInformation = true
  newFilter.departureDateTime = departureDateTime
  newFilter.returnDateTime = returnDateTime

  if (isDropOff) {
    newFilter.originLongName = null
    newFilter.destinationLongName = transferLongName
    newFilter.originGooglePlaceId = null
    newFilter.destinationGooglePlaceId = transferGooglePlaceId
  } else {
    newFilter.originLongName = transferLongName
    newFilter.destinationLongName = null
    newFilter.originGooglePlaceId = transferGooglePlaceId
    newFilter.destinationGooglePlaceId = null
  }
  return {
    ...state,
    [modeId]: newFilter,
  }
}

const setAllDepartureTimes = (state, hour, isOutbound) => {
  const newState = { ...state }
  // Can only use even numbers because that's what our time filter accepts
  // see FiltersTime.js
  const cleanHour = 2 * Math.floor(hour / 2)

  Object.keys(newState).forEach(modeId => {
    newState[modeId] = {
      ...newState[modeId],
    }
    if (isOutbound) {
      newState[modeId].outboundDepartureTime = cleanHour
      newState[modeId].isTimeManuallySet = true
    } else {
      newState[modeId].inboundDepartureTime = cleanHour
    }
  })

  return newState
}

// Used for when the time in the filter bar is updated
const setModeTime = (state, modeId, isOutbound, newDateTime) => {
  const newFilter = {
    ...state[modeId],
  }

  if (isOutbound) {
    newFilter.outboundDepartureTime = moment(newDateTime).hours()
  } else {
    newFilter.inboundDepartureTime = moment(newDateTime).hours()
  }

  return {
    ...state,
    [modeId]: newFilter,
  }
}

// Used for when the user checks or unchecks the 'include car' box
const setFerryType = (state, modeId, ferryType) => {
  const newFilter = {
    ...state[modeId],
    ferryType,
  }

  return {
    ...state,
    [modeId]: newFilter,
  }
}

//  We're not saving it to the reducer as we don't have the modeId yet
const setDefaultDate = (outbound, inbound) => {
  defaultDates.outbound = outbound
  if (inbound) {
    defaultDates.inbound = inbound
  }
}

// Used for when a schedules config comes back
const handleConfigReceived = (state, payload, extraParams) => {
  const newFilters = {}
  payload.transitKinds.forEach(kind => {
    // Default ferry type is foot if it's available
    let ferryType = null
    if (kind.supportsFerryFootPassenger) {
      ferryType = FERRY_TYPES.foot
    } else if (kind.supportsCarFootPassenger) {
      ferryType = FERRY_TYPES.car
    }

    let isTimeManuallySet = false
    if (state[kind.transitKind] && state[kind.transitKind].isTimeManuallySet != null) {
      isTimeManuallySet = state[kind.transitKind].isTimeManuallySet
    }

    const defaultOutboundTime = getDepartureTime(state[kind.transitKind], kind, true)
    const defaultInboundTime = getDepartureTime(state[kind.transitKind], kind, false)

    // Check to see if it's a airport transfer and we already have
    // Google Place ID
    let filterConfig = {}
    if (kind.transitKind === 'airport-transfer') {
      const departureDateTime = moment.utc(extraParams.outboundDateTime)
      departureDateTime.set({
        hour: defaultOutboundTime,
        minute: 0,
      })

      filterConfig = {
        originGooglePlaceId: extraParams.originGooglePlaceId,
        destinationGooglePlaceId: extraParams.destinationGooglePlaceId,
        originLongName: extraParams.originLongName,
        destinationLongName: extraParams.destinationLongName,
        departureDateTime,
      }

      if (extraParams.inboundDateTime) {
        const returnDateTime = moment.utc(extraParams.inboundDateTime)
        returnDateTime.set({
          hour: defaultInboundTime,
          minute: 0,
        })
        filterConfig.returnDateTime = returnDateTime
      }
    }

    newFilters[kind.transitKind] = {
      ...createNewFilter(kind.transitKind),
      outboundDepartureTime: defaultOutboundTime,
      inboundDepartureTime: defaultInboundTime,
      isTimeManuallySet: isTimeManuallySet,
      ferryType,
      ...filterConfig,
    }
  })

  return {
    ...state,
    ...newFilters,
  }
}

// persist selected times through searches
// - if it's time aware
//   - if have previous departure time, use it
//   - if don't have previous departure time, use default search hour
// - if not time aware return null
const getDepartureTime = (previousMode, kind, isOutbound) => {
  const isTimeAware = kind.hourOfDayAware

  let time
  if (isTimeAware && previousMode) {
    const previousTime = isOutbound
      ? previousMode.outboundDepartureTime
      : previousMode.inboundDepartureTime
    time = previousTime != null ? previousTime : DEFAULT_SEARCH_HOUR
  } else if (isTimeAware) {
    time = DEFAULT_SEARCH_HOUR
  } else {
    time = null
  }

  return time
}

export default filtersReducer
